import plLocale from "@fullcalendar/core/locales/pl";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, {
  EventDragStopArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import taskService from "api/taskService";
import CaseIcon from "assets/icons/case.svg?react";
import StickyIcon from "assets/icons/sticky-note.svg?react";
import DeskIcon from "assets/icons/desk.svg?react";
import { IninitialValueCreateActionModal } from "components/CreateActionModal/typings";
import ModalLayout, { DockPosition } from "components/ModalLayout";
import PackageCaseModal from "components/PackageCaseModal";
import ActionsContext from "contexts/ActionsContext";
import ModalsManagerContext from "contexts/ModalsManager";
import { IFeed } from "dto/IFeed";
import useCalendar from "hooks/useCalendar";
import moment from "moment";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { ENotificationType, notification } from "utils/notification";
import styles from "./styles.module.scss";
import { Props } from "./typings";
import TaskIcon from "assets/icons/task-alt.svg?react";

import "moment/dist/locale/pl";
import { ITask } from "dto/ITask";
import EventInfoModal from "components/EventInfoModal";
import TaskModal from "components/TaskModal";
import Popper from "components/Popper";
import { EventChangeArg } from "@fullcalendar/core";
import { useTranslation } from "react-i18next";
moment.locale("pl");

function stripHtml(html) {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  const decodedHtml = txt.value;

  return decodedHtml.replace(/<[^>]+>/g, "");
}

export function RenderEventContent(
  eventInfo: any,
  ref: any,
  handleChoose: (event: any, id: string) => void,
  handleClickOnCase: (caseId: string) => void,
  noTitleLabel = "(Brak Tytułu)",
) {
  const props = eventInfo.event._def.extendedProps;
  const range = eventInfo.event._instance.range;

  const isMoreThanHalf = moment(range.end).isAfter(
    moment(range.start).add(30, "minute"),
  );

  const isCases = window.location.href.search("cases") !== -1;

  return (
    <div
      onClick={(e) => {
        handleChoose(e, eventInfo.event.id);
      }}
      className={styles.test}
    >
      <div
        style={{
          background: eventInfo.event.backgroundColor
            ? `${eventInfo.event.backgroundColor.substring(0, 7)}`
            : undefined,
        }}
        className={`${styles.color} ${props.type === 1 && styles.isTask}`}
      ></div>
      <div className={styles.eventWrapper}>
        <div className={styles.header}>
          <div className={styles.title}>
            {props.type === 1 && <TaskIcon />}
            {eventInfo.event.title.length
              ? eventInfo.event.title
              : noTitleLabel}
          </div>
          <div ref={ref} className={styles.icons}>
            {props.isDeskActive && (
              <DeskIcon
                onClick={(e) => {
                  handleClickOnCase(props.caseId);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={styles.icon}
              />
            )}
            {props.caseId && !isCases && (
              <CaseIcon
                onClick={(e) => {
                  handleClickOnCase(props.caseId);
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={styles.icon}
              />
            )}
            {!!props?.note?.length && <StickyIcon className={styles.icon} />}
          </div>
        </div>
        {isMoreThanHalf && (
          <div
            data-tooltip-id={`bar-${eventInfo.event.id}`}
            className={styles.description}
          >
            {stripHtml(props?.note ?? "")}
          </div>
        )}
      </div>
      <Tooltip
        place="bottom"
        style={{
          maxWidth: "400px",
          wordBreak: "break-all",
          display: "flex",
          whiteSpace: "normal",
        }}
        id={`bar-${eventInfo.event.id}`}
      >
        {props.note}
      </Tooltip>
    </div>
  );
}

const ActionsCalendar: FC<Props> = ({
  date,
  type,
  showCase = true,
  initialValues,
  caseId: initialCaseId,
  handleChangeDate,
  search,
  calendars,
  onlyFromCourtPortal,
}) => {
  const { t } = useTranslation();
  const { calendarView } = useParams();
  const { dates, setDates } = useCalendar();
  const [position, setPosition] = useState([0, 0]);
  const [showModal, setShowModal] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const { data, refetch } = taskService.useGetFeed(
    dates.start,
    dates.end,
    initialCaseId,
    search,
    calendars,
  );
  const { mutate: moveTask } = taskService.useMoveTask();

  const iconsWrapper = useRef(null);

  const [caseId, setCaseId] = useState<string | null>(null);

  const { modals, setModals } = useContext(ModalsManagerContext);

  const [eventInfo, setEventInfo] = useState<IFeed | null>(null);

  const [initialValue, setInitialValue] =
    useState<IninitialValueCreateActionModal | null>(null);

  // Jedna firma na użytkownika, bez przełącznika kontekstu (decyzja D1) — nie ma już
  // zdarzenia "zmiana firmy" w trakcie sesji, więc czyszczenie modala/stanu tworzenia akcji
  // po takiej zmianie jest zbędne (dawne useOnProfileChange nigdy by się tu nie odpaliło).

  const { tasks, setTasks } = useContext(ActionsContext);

  const ref = useRef(null);
  const events = useMemo(
    () =>
      tasks?.map((event: IFeed) => ({
        editable: !event.readOnly,
        IsFavourite: event.IsFavourite,
        note: event.description ?? event.Note,
        allDay: event.allDay ?? event.AllDay,
        id: event.id ?? event.Id,
        title: event.title ?? event.Title ?? "",
        caseId: event?.case?.Id ?? event.Case?.Id ?? null,
        isDeskActive: event?.IsDeskActive,
        type: event?.type ?? 0,
        start: moment(event.start ?? event.FromDate).toDate(),
        end:
          event.end || event.ToDate
            ? moment(event.end ?? event.ToDate).toDate()
            : moment(event.start ?? event.FromDate)
                .endOf("day")
                .add(1, "m")
                .toDate(),
        backgroundColor: event.color ? `${event.color}19` : undefined,
      })) ?? [],
    [tasks],
  );

  useEffect(() => {
    if (!data) return;
    setTasks(data.data);
  }, [data]);

  const dateClicked = (day: any) => {
    setEndDate(null);
    setInitialValue(null);
    setShowModal(day.date);
    sessionStorage.setItem("action-date", day.date.toString());
  };

  const handleClickOnCase = (caseId: string) => {
    setCaseId(caseId);
  };

  useEffect(() => {
    if (!ref?.current) return;
    //@ts-ignore
    ref.current.getApi().gotoDate(moment(date).subtract(1, "day").toDate());

    if (calendarView === "day") {
      //@ts-ignore
      ref.current.getApi().gotoDate(date);
      setDates({
        start: moment(date).startOf("day").toISOString(),
        end: moment(date).endOf("day").toISOString(),
      });
      return;
    }

    if (calendarView === "week") {
      //@ts-ignore
      ref.current.getApi().gotoDate(moment(date).startOf("week").toISOString());
      setDates({
        start: moment(date).startOf("week").toISOString(),
        end: moment(date).startOf("week").add(7, "day").toISOString(),
      });
    }
    if (!calendarView) {
      setDates({
        start: moment(date).startOf("month").toISOString(),
        end: moment(date).add(34, "d").toISOString(),
      });
    }
  }, [date]);

  const [view, setView] = useState<
    "dayGridWeek" | "dayGridMonth" | "dayGridDay" | "listWeek"
  >("dayGridMonth");
  const [day, setDays] = useState(1);

  useEffect(() => {
    if (!ref?.current) return;

    if (calendarView === "day" || type === "dayGridDay") {
      //@ts-ignore
      ref.current
        //@ts-ignore
        .getApi()
        .changeView("timeGridFourDay");
      setView("dayGridDay");
      setDays(1);
      return;
    }
    if (calendarView === "week" || type === "dayGridWeek") {
      //@ts-ignore
      ref.current
        //@ts-ignore
        .getApi()
        .changeView("timeGridFourDay");
      setView("dayGridWeek");
      setDays(7);

      const currentDate = ref.current.getApi().getDate();
      const dayOfWeek = currentDate.getDay(); // 0 - Sunday, 1 - Monday, ..., 6 - Saturday
      const monday = new Date(currentDate);
      monday.setDate(currentDate.getDate() - ((dayOfWeek + 6) % 7)); // Calculate the previous Monday
      ref.current.getApi().gotoDate(monday); // Go to that Monday

      return;
    }
    if (calendarView === "month" || type === "dayGridMonth") {
      //@ts-ignore
      ref.current
        //@ts-ignore
        .getApi()
        .changeView("dayGridMonth");
      setView("dayGridMonth");
      return;
    }
  }, [ref, calendarView, type]);

  const handleChoose = (e: any, id: string) => {
    const tagName = e.target.tagName;

    if (tagName === "svg") {
      return;
    }
    console.log("clicked", id);

    const c = tasks?.find((c: any) => c.id === id || c.Id === id);
    if (!c) return;

    // setEdit(c);
    // setInitialValue({
    //   id: c.id,
    //   //@ts-ignore
    //   case: c,
    //   note: c.note ?? c.title,
    //   fullday: c.allDay,
    // });

    setEventInfo(c);
    setPosition([e.clientX, e.clientY]);
    // localStorage.setItem("action", JSON.stringify(c));
    // setModals({ ...modals, createTask: true });
  };

  const refe = useRef(null);

  const handleEventDrop = (e: any) => {
    console.log("Event drop", e);
    moveTask(
      {
        Id: e.event.id,
        AllDay: false,
        End: moment(e.event.end).toISOString(),
        Start: moment(e.event.start).toISOString(),
      },
      {
        onError: () => {
          notification({
            type: ENotificationType.ERROR,
            title: t("errors.somethingWentWrong"),
            text: t("errors.cannotUpdateAction"),
          });
        },
      },
    );

    // const minutes = moment(e.date).diff()
  };

  const handleEventDragStop = (e: EventDragStopArg) => {
    console.log("Event drag stop", e);
    moveTask(
      {
        Id: e.event.id,
        AllDay: false,
        End: e.event.end.toISOString(),
        Start: e.event.start.toISOString(),
      },
      {
        onError: () => {
          notification({
            type: ENotificationType.ERROR,
            title: t("errors.somethingWentWrong"),
            text: t("errors.cannotUpdateAction"),
          });
        },
      },
    );

    // const minutes = moment(e.date).diff()
  };

  const handleEventChange = (e: EventChangeArg) => {
    console.log("Event change", e);
    moveTask(
      {
        Id: e.event.id,
        AllDay: false,
        End: e.event.end.toISOString(),
        Start: e.event.start.toISOString(),
      },
      {
        onError: () => {
          notification({
            type: ENotificationType.ERROR,
            title: t("errors.somethingWentWrong"),
            text: t("errors.cannotUpdateAction"),
          });
        },
      },
    );

    // const minutes = moment(e.date).diff()
  };

  const handleEventResize = (e: EventResizeDoneArg) => {
    moveTask(
      {
        Id: e.event.id,
        AllDay: false,
        End: moment(e.event.end).toISOString(),
        Start: moment(e.event.start).toISOString(),
      },
      {
        onError: () => {
          notification({
            type: ENotificationType.ERROR,
            title: t("errors.somethingWentWrong"),
            text: t("errors.cannotUpdateAction"),
          });
        },
      },
    );
  };

  return (
    <div ref={refe} className={styles.wrapper}>
      {caseId && (
        <Popper
          anchorEl={iconsWrapper?.current}
          setOpen={() => setCaseId(null)}
          open={true}
        >
          <PackageCaseModal CaseId={caseId} toggle={() => setCaseId(null)} />
        </Popper>
      )}
      <FullCalendar
        locale={plLocale}
        slotEventOverlap={view === "dayGridWeek" ? true : false}
        headerToolbar={{
          start: "today",
          center: "prev title next",
          end: "",
        }}
        views={{
          timeGridFourDay: {
            type: "timeGrid",
            firstDay: 1,
            duration: { days: day },
            // eventMaxStack: 1,
          },
        }}
        dayHeaderFormat={(date) => {
          if (view === "dayGridWeek") {
            return moment(date.start).format("dd.DD");
          }

          if (view === "dayGridMonth") {
            return moment(date.start).format("dd");
          }
        }}
        titleFormat={(date) => {
          if (view === "dayGridDay") {
            return `${moment(date.start).format("MMMM").toString()} ${moment(
              date.start,
            ).format("DD, dd")}`;
          }

          if (view === "dayGridWeek") {
            return `${moment(date.start).format("MMMM DD")} - ${moment(
              date.end,
            ).format("DD")}`;
          }

          return moment(date.start).format("MMMM");
        }}
        slotLabelFormat={(date) => {
          if (date.date.minute == 0)
            return date.date.hour.toString().padStart(2, "0") + ":00";

          return date.date.minute;
        }}
        dayMaxEvents={3}
        dayMaxEventRows={true}
        initialView={type ? type : view}
        ref={ref}
        nowIndicator={true}
        firstDay={1}
        plugins={[dayGridPlugin, interactionPlugin, listPlugin, timeGridPlugin]}
        weekends={true}
        events={events}
        allDayText={t("calendar.allDayShort")}
        droppable={true}
        eventReceive={handleEventDrop}
        weekNumberClassNames={styles.trt}
        datesSet={(event) => {
          const startDate = moment(event.start).toISOString();
          const endDate = moment(event.end).toISOString();

          handleChangeDate?.(
            moment(event.start).add(1, "day").toDate(),
            event.end,
          );
          setDates({
            start: startDate,
            end: endDate,
          });
        }}
        viewClassNames={view !== "dayGridMonth" && styles.removeBorder}
        dayHeaderClassNames={view === "dayGridDay" && styles.removeDay}
        displayEventTime
        dateClick={dateClicked}
        select={(e) => {
          if (onlyFromCourtPortal) {
            return;
          }
          setEndDate(e.end);
          setInitialValue(null);
          localStorage.setItem(
            "createOrEditDate",
            JSON.stringify([e.start, e.end]),
          );
          localStorage.setItem("allDay", JSON.stringify(e.allDay));

          setModals({ ...modals, createEvent: true, hide: false });
        }}
        selectMinDistance={0}
        editable={true}
        dropAccept={"true"}
        selectable={true}
        eventResize={handleEventResize}
        // eventDragStop={handleEventDragStop}
        eventChange={handleEventChange}
        allDayClassNames={styles.event}
        eventClassNames={styles.event}
        eventContent={(event: any) =>
          RenderEventContent(
            event,
            iconsWrapper,
            handleChoose,
            handleClickOnCase,
            t("common.noTitle"),
          )
        }
      />
      {eventInfo && eventInfo.type === 0 && (
        <EventInfoModal
          position={position}
          event={eventInfo}
          toggle={() => setEventInfo(null)}
          refetch={() => {
            refetch();
          }}
        />
      )}

      {eventInfo && eventInfo.type === 1 && (
        <TaskModal
          task={eventInfo as ITask}
          setTask={() => setEventInfo(null)}
        />
      )}
    </div>
  );
};

export default ActionsCalendar;
